import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Stack,
  Snackbar,
  Alert,
  Paper,
  Avatar,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import api from "../../api/axios";

const ArchivedProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [undoData, setUndoData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  /* ================= FETCH ================= */
  const fetchArchived = async () => {
    try {
      const { data } = await api.get("/products/archived");
      setProducts(data.products);
    } catch (err) {
      console.error("Fetch archived failed:", err);
    }
  };

  useEffect(() => {
    fetchArchived();
  }, []);

  /* ── Helper: extract first image thumbnail URL from product ── */
  const getThumbnail = (product) => {
    if (product.media?.length) {
      const img = product.media.find(m => m.type === 'image' || m.mimeType?.startsWith('image/'));
      if (img) return img.thumbnailUrl || img.url || '';
      return product.media[0]?.thumbnailUrl || product.media[0]?.url || '';
    }
    if (product.images?.length) {
      const img = product.images[0];
      if (typeof img === 'string') return img;
      return img.thumbnailUrl || img.url || '';
    }
    return '';
  };

  /* ================= SELECT ================= */
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id]
    );
  };

  const isAllSelected =
    products.length > 0 && selected.length === products.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected(products.map((p) => p._id));
    }
  };

  /* ================= ACTIONS ================= */
  const handleRestore = async (id) => {
    await api.patch(`/products/${id}/restore`);

    setUndoData({ ids: [id] });
    setSnackbarOpen(true);

    fetchArchived();
  };

  const handleBulkRestore = async () => {
    if (!selected.length) return;

    await api.patch("/products/bulk/restore", {
      ids: selected,
    });

    setUndoData({ ids: selected });
    setSelected([]);
    setSnackbarOpen(true);

    fetchArchived();
  };

  const handleUndo = async () => {
    if (!undoData) return;

    await api.patch("/products/bulk/archive", {
      ids: undoData.ids,
    });

    setSnackbarOpen(false);
    fetchArchived();
  };

  /* ================= UI ================= */
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ================= HEADER (matches Dashboard / VendorOrders) ================= */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button onClick={() => navigate("/vendor/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 0.5, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>📦 Archived Products</Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>View and restore products that have been archived.</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
        {/* ================= Section Header ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Archived Products
          </Typography>
        </Box>

        {/* 🔹 Bulk Actions */}
        <Stack direction="row" spacing={2} mb={2} alignItems="center">
          <Checkbox
            checked={isAllSelected}
            indeterminate={
              selected.length > 0 && selected.length < products.length
            }
            onChange={toggleSelectAll}
          />

          <Button
            variant="contained"
            disabled={!selected.length}
            onClick={handleBulkRestore}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.8rem',
            }}
          >
            Restore Selected
          </Button>
        </Stack>

        {/* 🔹 Product List */}
        <Stack spacing={2}>
          {products.map((p) => (
            <Paper
              key={p._id}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: '12px',
                border: '1.5px solid #f1f5f9',
                boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
                transition: 'transform 0.18s ease, boxShadow 0.18s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Checkbox
                  checked={selected.includes(p._id)}
                  onChange={() => toggleSelect(p._id)}
                />

                <Avatar
                  src={getThumbnail(p)}
                  sx={{ width: 64, height: 64, borderRadius: 1, bgcolor: '#e0e0e0', fontSize: '1.1rem', fontWeight: 600 }}
                >
                  {p.title?.[0]?.toUpperCase() || 'P'}
                </Avatar>
                <Typography sx={{ fontWeight: 500, color: '#1e293b' }}>{p.title}</Typography>
              </Stack>

              <Button
                variant="outlined"
                onClick={() => handleRestore(p._id)}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                Restore
              </Button>
            </Paper>
          ))}
        </Stack>

        {/* 🔹 Undo Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            severity="success"
            action={
              <Button color="inherit" size="small" onClick={handleUndo}>
                UNDO
              </Button>
            }
          >
            Products restored
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ArchivedProducts;